import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export interface CreateEpreuveInput {
  title: string;
  description?: string | null;
  subject?: SubjectSlug | null;
  level: SchoolLevel;
  exam_type?: string | null;
  exam_year?: number | null;
  source_file_url?: string | null;
  /** PDF du corrigé officiel (source de vérité des corrections). */
  correction_file_url?: string | null;
}

export function useCreateEpreuve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEpreuveInput) => {
      const { data, error } = await supabase
        .from('epreuves')
        .insert({
          title: input.title,
          description: input.description ?? null,
          subject: input.subject ?? null,
          level: input.level,
          exam_type: input.exam_type ?? null,
          exam_year: input.exam_year ?? null,
          source_file_url: input.source_file_url ?? null,
          correction_file_url: input.correction_file_url ?? null,
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-epreuves'] });
    },
  });
}

export type UpdateEpreuveInput = Partial<CreateEpreuveInput> & {
  id: string;
  status?: 'draft' | 'published' | 'archived';
  ocr_status?: 'pending' | 'processing' | 'done' | 'failed';
};

/**
 * Lance le pipeline d'extraction V2 d'une épreuve (énoncé → exercices V2,
 * corrigé → corrections, distracteurs). Invoque l'edge fn `epreuve-extract`
 * qui traite en arrière-plan ; l'UI suit ensuite `pipeline_status`.
 */
export function useLaunchEpreuvePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (epreuveId: string) => {
      const { error } = await supabase.functions.invoke('epreuve-extract', {
        body: { epreuveId },
      });
      // Le timeout HTTP (504) est attendu : la fonction continue en arrière-plan.
      if (error) {
        const msg = error.message ?? '';
        const isTimeout = /504|timeout|closed|non-2xx/i.test(msg);
        if (!isTimeout) throw new Error(msg);
      }
    },
    onSuccess: (_data, epreuveId) => {
      qc.invalidateQueries({ queryKey: ['admin-epreuve-detail', epreuveId] });
      qc.invalidateQueries({ queryKey: ['admin-epreuves'] });
    },
  });
}

export function useUpdateEpreuve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateEpreuveInput) => {
      const { error } = await supabase.from('epreuves').update(patch).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['admin-epreuves'] });
      qc.invalidateQueries({ queryKey: ['admin-epreuve-detail', id] });
    },
  });
}

export function useDeleteEpreuve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('epreuves').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-epreuves'] });
    },
  });
}

export function useAddExerciseToEpreuve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      epreuveId,
      exerciseId,
      displayOrder,
    }: {
      epreuveId: string;
      exerciseId: string;
      displayOrder: number;
    }) => {
      const { error } = await supabase
        .from('epreuve_exercises')
        .insert({ epreuve_id: epreuveId, exercise_id: exerciseId, display_order: displayOrder });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { epreuveId }) => {
      qc.invalidateQueries({ queryKey: ['admin-epreuve-detail', epreuveId] });
    },
  });
}

export function useRemoveExerciseFromEpreuve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ epreuveId, exerciseId }: { epreuveId: string; exerciseId: string }) => {
      const { error } = await supabase
        .from('epreuve_exercises')
        .delete()
        .eq('epreuve_id', epreuveId)
        .eq('exercise_id', exerciseId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { epreuveId }) => {
      qc.invalidateQueries({ queryKey: ['admin-epreuve-detail', epreuveId] });
      qc.invalidateQueries({ queryKey: ['admin-epreuves'] });
    },
  });
}

export function useReorderEpreuveExercises() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      epreuveId,
      orderedExerciseIds,
    }: {
      epreuveId: string;
      orderedExerciseIds: string[];
    }) => {
      const updates = orderedExerciseIds.map((exerciseId, idx) =>
        supabase
          .from('epreuve_exercises')
          .update({ display_order: idx })
          .eq('epreuve_id', epreuveId)
          .eq('exercise_id', exerciseId),
      );
      const results = await Promise.all(updates);
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw new Error(firstError.message);
    },
    onSuccess: (_data, { epreuveId }) => {
      qc.invalidateQueries({ queryKey: ['admin-epreuve-detail', epreuveId] });
    },
  });
}
